import React, { forwardRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import useCountUp from "../../hooks/useCountUp";
import { MAX_SCORE } from "../../data/assessment";
import FeedbackLoop from "./FeedbackLoop";

const RING_DIAMETER = 180;
const STROKE = 10;
const RADIUS = (RING_DIAMETER - STROKE) / 2; // 85
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const ResultReveal = forwardRef(function ResultReveal(
  { score, band, visible, onCalendarClick },
  ref,
) {
  const count = useCountUp(score, visible, 1200);
  const [ringFilled, setRingFilled] = useState(false);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setRingFilled(true), 60);
      return () => clearTimeout(t);
    }
    setRingFilled(false);
    return undefined;
  }, [visible]);

  if (!band) return null;

  const pct = score / MAX_SCORE;
  const offset = ringFilled ? CIRCUMFERENCE * (1 - pct) : CIRCUMFERENCE;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "var(--rtd-navy)", minHeight: "100vh" }}
      data-testid="result-section"
    >
      <div className="grain-overlay" aria-hidden="true" />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "12%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 640,
          height: 640,
          background:
            "radial-gradient(circle, rgba(232,160,32,0.14) 0%, rgba(11,25,41,0) 60%)",
          pointerEvents: "none",
        }}
      />

      <div
        className="relative mx-auto flex flex-col items-center text-center"
        style={{
          maxWidth: 680,
          minHeight: "100vh",
          justifyContent: "center",
          padding: "96px 24px",
        }}
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.4 }}
          className="font-body"
          style={{
            fontWeight: 700,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "var(--rtd-on-dark-soft)",
            marginBottom: 32,
          }}
        >
          Your Result
        </motion.p>

        {/* 1. Ring + score */}
        <div
          className="relative flex items-center justify-center"
          style={{ width: RING_DIAMETER, height: RING_DIAMETER, marginBottom: 12 }}
          data-testid="score-ring"
        >
          <svg
            className="score-ring"
            width={RING_DIAMETER}
            height={RING_DIAMETER}
            viewBox={`0 0 ${RING_DIAMETER} ${RING_DIAMETER}`}
            style={{ transform: "rotate(-90deg)", position: "absolute", inset: 0 }}
          >
            <circle
              cx={RING_DIAMETER / 2}
              cy={RING_DIAMETER / 2}
              r={RADIUS}
              fill="none"
              stroke="#1A2E44"
              strokeWidth={STROKE}
            />
            <circle
              cx={RING_DIAMETER / 2}
              cy={RING_DIAMETER / 2}
              r={RADIUS}
              fill="none"
              stroke="var(--rtd-gold)"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
            />
          </svg>
          <span
            className="font-serif-display text-[80px] sm:text-[128px]"
            style={{ color: "var(--rtd-gold)", lineHeight: 0.9 }}
            data-testid="score-number"
          >
            {count}
          </span>
        </div>
        <p className="font-body" style={{ fontSize: 14, color: "#7A94A8", marginBottom: 20 }}>
          out of {MAX_SCORE}
        </p>

        {/* 2. Band pill */}
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 1.2 }}
          className="font-body inline-block"
          data-testid="result-band-pill"
          style={{
            fontWeight: 700,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            padding: "5px 14px",
            borderRadius: 20,
            backgroundColor: band.pillBg,
            color: band.pillText,
            border: `1px solid ${band.pillBorder}`,
            marginBottom: 28,
          }}
        >
          {band.name}
        </motion.span>

        {/* 3. Three metric cards */}
        <div
          className="grid w-full grid-cols-1 sm:grid-cols-3"
          style={{ gap: 16, maxWidth: 640 }}
        >
          {band.cards.map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 1.4 + i * 0.1 }}
              className="text-left"
              data-testid={`result-metric-card-${i + 1}`}
              style={{
                backgroundColor: "var(--rtd-navy-card)",
                border: "1px solid var(--rtd-navy-border)",
                borderRadius: 12,
                padding: 20,
              }}
            >
              <div
                className="font-body"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "#7A94A8",
                  marginBottom: 8,
                }}
              >
                {card.label}
              </div>
              <div
                className="font-serif-display"
                style={{ fontSize: 28, color: "var(--rtd-gold)", lineHeight: 1.1, marginBottom: 8 }}
              >
                {card.value}
              </div>
              <div
                className="font-body"
                style={{ fontSize: 13, color: "#7A94A8", lineHeight: 1.5 }}
              >
                {card.desc}
              </div>
            </motion.div>
          ))}
        </div>

        {/* 4. Feedback loop infographic */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 1.75 }}
          className="w-full"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <FeedbackLoop />
        </motion.div>

        {/* 5. Result headline */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.9 }}
          className="font-serif-display text-[26px] sm:text-[32px]"
          style={{ color: "var(--rtd-on-dark)", lineHeight: 1.2, maxWidth: 600, marginTop: 12 }}
          data-testid="result-headline"
        >
          {band.headline}
        </motion.h2>

        {/* 6. Result body */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 2.0 }}
          className="font-body"
          style={{
            fontSize: 16,
            color: "var(--rtd-on-dark-soft)",
            lineHeight: 1.7,
            maxWidth: 560,
            marginTop: 20,
          }}
          data-testid="result-body"
        >
          {band.body}
        </motion.p>

        {/* 7. CTA button */}
        <motion.button
          type="button"
          onClick={() => onCalendarClick && onCalendarClick("result_section")}
          data-testid="result-cta-book-button"
          initial={{ opacity: 0, y: 16 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 2.15 }}
          whileHover={{ opacity: 0.9 }}
          className="font-body"
          style={{
            marginTop: 32,
            backgroundColor: "var(--rtd-gold)",
            color: "var(--rtd-navy)",
            fontWeight: 700,
            fontSize: 16,
            padding: "16px 32px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            transition: "opacity 200ms ease",
            boxShadow: "0 12px 32px rgba(232,160,32,0.25)",
          }}
        >
          Book a 20-minute signal diagnostic →
        </motion.button>
      </div>
    </section>
  );
});

export default ResultReveal;
